import { Entity, TransportModes } from '@nx-smartmonkey/shared/domain';
import { VehicleProjectFieldType } from './DriverProjectFields';

export type VehicleProjectConfigurationTypes =
  | `driver_position`
  | `stop_duration`
  | `pin`
  | `pictures_gallery`
  | `survey`;

export type VehicleProjectConfiguration = VehicleProjectConfigurationTypes[];

export type DistanceType = `km` | `mi`;
export type WeightType = `kg` | `lb`;
export type VolumeType = `l` | `m³` | `u`;

export interface DriverProjectFieldElement {
  id: string;
  label: string;
  type: VehicleProjectFieldType;
  is_custom_field: boolean;
  options?: string[];
  is_required?: boolean;
}

export type DriverProjectFields = Array<DriverProjectFieldElement>;

export interface DriversProjectFields {
  configuration: VehicleProjectConfiguration;
  stop_fields: DriverProjectFields;
  task_fields: DriverProjectFields;
  stop_completed_fields: DriverProjectFields;
  stop_canceled_fields: DriverProjectFields;
  task_completed_fields: DriverProjectFields;
  task_canceled_fields: DriverProjectFields;
}

export interface ProjectTaskPreferences {
  barcode: boolean;
  comments: boolean;
}

export interface DriversProjectUnits {
  distance: DistanceType;
  weight: WeightType;
  volume: VolumeType;
}

export interface DriversProjectProps {
  fields: DriversProjectFields;
  units: DriversProjectUnits;
  tasks_enabled: boolean;
  stop_reports_enabled: boolean;
  time_zone: string;
  service_duration: number;
  stop_grouping: boolean;
  use_traffic: boolean;
  transport_mode?: TransportModes;
}

export interface DirverProjectUpdateFields {
  distance_unit?: string;
  stop_duration?: number;
  use_traffic?: boolean;
  stop_grouping?: boolean;
  transport_mode?: TransportModes;
}

export class DriversProject extends Entity<DriversProjectProps> {
  get id(): string {
    return this._id!;
  }
  get fields(): DriversProjectFields {
    return this.props.fields;
  }
  get units(): DriversProjectUnits {
    return this.props.units;
  }
  get tasks_enabled(): boolean {
    return this.props.tasks_enabled;
  }
  get stop_reports_enabled(): boolean {
    return this.props.stop_reports_enabled;
  }
  get survey_drivers(): boolean {
    return this.fields.configuration.includes(`survey`);
  }
  get pin_enabled(): boolean {
    return this.fields.configuration.includes(`pin`);
  }
  get gallery_enabled(): boolean {
    return this.fields.configuration.includes(`pictures_gallery`);
  }
  get driver_position(): boolean {
    return this.fields.configuration.includes(`driver_position`);
  }
  get stop_duration(): boolean {
    return this.fields.configuration.includes(`stop_duration`);
  }
  get time_zone(): string {
    return this.props.time_zone;
  }
  get use_traffic(): boolean {
    return this.props.use_traffic;
  }
  get service_duration(): number {
    return this.props.service_duration;
  }
  get stop_grouping(): boolean {
    return this.props.stop_grouping;
  }
  get transport_mode(): TransportModes | undefined {
    return this.props.transport_mode;
  }

  get stop_fields_with_sections(): {
    information: DriverProjectFields;
    contact: DriverProjectFields;
    custom_fields: DriverProjectFields;
    constraints: DriverProjectFields;
  } {
    const stopFields = {
      information: [] as DriverProjectFields,
      contact: [] as DriverProjectFields,
      custom_fields: [] as DriverProjectFields,
      constraints: [] as DriverProjectFields,
    };

    const informationFields = [`external_id`, `location_details`, `reference_person`];
    const contactFields = [`phone`, `email`, `reference_person`, `url`, `comments`];
    const constraintsFields = [`weight`, `volume`, `time_windows`, `requires`];
    return this.fields.stop_fields.reduce((acc, field) => {
      if (field.is_custom_field) {
        acc.custom_fields.push(field);
        return acc;
      }

      if (informationFields.includes(field.id)) {
        acc.information.push(field);
      } else if (contactFields.includes(field.id)) {
        acc.contact.push(field);
      } else if (constraintsFields.includes(field.id)) {
        acc.constraints.push(field);
      }
      return acc;
    }, stopFields);
  }

  update({ distance_unit, stop_duration, stop_grouping, transport_mode, use_traffic }: DirverProjectUpdateFields) {
    if (distance_unit !== undefined) {
      this.props.units.distance = distance_unit as DistanceType;
    }
    if (stop_duration !== undefined) {
      this.props.service_duration = stop_duration;
    }
    if (stop_grouping !== undefined) {
      this.props.stop_grouping = stop_grouping;
    }
    if (transport_mode !== undefined) {
      this.props.transport_mode = transport_mode;
    }
    if (use_traffic !== undefined) {
      this.props.use_traffic = use_traffic;
    }
  }

  private constructor(props: DriversProjectProps, id: string) {
    super(props, id);
  }

  static create(props: DriversProjectProps, id: string): DriversProject {
    return new DriversProject(props, id);
  }
}
